import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { canViewHR } from "@/lib/helpers";
import {
  PageHeader, BtnPrimary, BtnSecondary, BtnGhost, Modal, Field, Input, Textarea, Empty,
} from "@/components/primitives";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Clock } from "@phosphor-icons/react";

const BLANK = {
  name: "",
  pattern: "",
  shift: "",
  start_time: "06:00",
  end_time: "17:00",
  off_days: "",
  hours_per_day: 8,
  working_days_per_month: 22,
  notes: "",
};

const PRESETS = [
  { name: "5/2 · Day Shift Only", pattern: "5/2", shift: "Day shift Only",
    start_time: "06:00", end_time: "17:00", off_days: "2 off days",
    hours_per_day: 11, working_days_per_month: 22 },
  { name: "10/5 · Day Shift 5 days", pattern: "10/5", shift: "Day shift 5 days",
    start_time: "06:00", end_time: "17:00", off_days: "5 off days",
    hours_per_day: 11, working_days_per_month: 20 },
  { name: "10/5 · Night Shift 5 days", pattern: "10/5", shift: "Night shift 5 days",
    start_time: "17:00", end_time: "06:00", off_days: "5 off days",
    hours_per_day: 13, working_days_per_month: 20 },
];

export default function Rosters() {
  const { user } = useAuth();
  const canEdit = canViewHR(user.role);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(BLANK);

  async function load() {
    try { const { data } = await api.get("/rosters"); setRows(data); }
    catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  function openNew() { setEdit(null); setForm(BLANK); setOpen(true); }
  function openEdit(r) { setEdit(r); setForm({ ...BLANK, ...r }); setOpen(true); }
  function applyPreset(p) { setForm({ ...form, ...p }); }

  async function save() {
    if (!form.name) return toast.error("Name is required");
    try {
      const payload = {
        ...form,
        hours_per_day: Number(form.hours_per_day || 0),
        working_days_per_month: Number(form.working_days_per_month || 0),
      };
      if (edit) await api.put(`/rosters/${edit.id}`, payload);
      else await api.post("/rosters", payload);
      toast.success(edit ? "Roster updated" : "Roster created");
      setOpen(false); load();
    } catch (e) { toast.error(formatError(e)); }
  }

  async function remove(r) {
    if (!window.confirm(`Delete roster "${r.name}"?`)) return;
    try { await api.delete(`/rosters/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatError(e)); }
  }

  return (
    <div data-testid="rosters-page">
      <PageHeader
        overline="Module / HR"
        title="Work Rosters"
        subtitle="Shift patterns HR assigns to each employee — drives daily hours & payroll math."
        actions={canEdit && (
          <BtnPrimary onClick={openNew} data-testid="roster-new">
            <Plus size={14} weight="bold" className="inline mr-1.5" /> New Roster
          </BtnPrimary>
        )}
      />

      <div className="phizo-card mb-6">
        {loading ? (
          <div className="p-5 phizo-label">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-5">
            <Empty label="No rosters yet. Add the 5/2 Day, 10/5 Day and 10/5 Night patterns to get started." />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="phizo-table">
              <thead>
                <tr>
                  <th>Roster</th><th>Shift</th><th>Start</th><th>End</th>
                  <th>Off Days</th>
                  <th className="text-right">Hrs / Day</th>
                  <th className="text-right">Days / Month</th>
                  {canEdit && <th className="text-right">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`roster-row-${r.id}`}>
                    <td>
                      <div className="font-medium flex items-center gap-2"><Clock size={14} weight="bold" /> {r.name}</div>
                      <div className="text-xs text-[#a1a1aa] mono">{r.pattern || "—"}</div>
                    </td>
                    <td className="text-sm">{r.shift || "—"}</td>
                    <td className="mono text-sm">{r.start_time || "—"}</td>
                    <td className="mono text-sm">{r.end_time || "—"}</td>
                    <td className="text-sm text-[#52525b]">{r.off_days || "—"}</td>
                    <td className="text-right mono">{r.hours_per_day}</td>
                    <td className="text-right mono">{r.working_days_per_month}</td>
                    {canEdit && (
                      <td className="text-right whitespace-nowrap">
                        <BtnGhost onClick={() => openEdit(r)} data-testid={`roster-edit-${r.id}`}>
                          <PencilSimple size={12} weight="bold" />
                        </BtnGhost>
                        {user.role === "admin" && (
                          <BtnGhost onClick={() => remove(r)} className="text-[#FF2A2A]"
                            data-testid={`roster-delete-${r.id}`}>
                            <Trash size={12} weight="bold" />
                          </BtnGhost>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} wide
        title={edit ? `Edit ${edit.name}` : "New Work Roster"} testId="roster-modal">
        {!edit && (
          <div className="mb-4 border border-[#e4e4e7] p-3 bg-[#f4f4f5]">
            <div className="phizo-label mb-2">Quick Presets</div>
            <div className="flex flex-wrap gap-2">
              {PRESETS.map((p) => (
                <button key={p.name} type="button"
                  onClick={() => applyPreset(p)}
                  className="text-xs px-3 py-1 border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white"
                  data-testid={`roster-preset-${p.pattern}-${p.shift.replace(/\s+/g, '-').toLowerCase()}`}>
                  {p.name}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name *">
            <Input value={form.name} data-testid="roster-name"
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. 5/2 Day Shift" />
          </Field>
          <Field label="Pattern">
            <Input value={form.pattern}
              onChange={(e) => setForm({ ...form, pattern: e.target.value })}
              placeholder="5/2  ·  10/5" />
          </Field>
          <Field label="Shift">
            <Input value={form.shift}
              onChange={(e) => setForm({ ...form, shift: e.target.value })}
              placeholder="Day shift Only" />
          </Field>
          <Field label="Off Days">
            <Input value={form.off_days}
              onChange={(e) => setForm({ ...form, off_days: e.target.value })}
              placeholder="2 off days" />
          </Field>
          <Field label="Start Time">
            <Input type="time" value={form.start_time}
              onChange={(e) => setForm({ ...form, start_time: e.target.value })} />
          </Field>
          <Field label="End Time">
            <Input type="time" value={form.end_time}
              onChange={(e) => setForm({ ...form, end_time: e.target.value })} />
          </Field>
          <Field label="Hours per day" hint="Standard hours worked per shift">
            <Input type="number" step="0.5" value={form.hours_per_day}
              data-testid="roster-hours-per-day"
              onChange={(e) => setForm({ ...form, hours_per_day: e.target.value })} />
          </Field>
          <Field label="Working days per month" hint="Used to compute hourly rate from monthly salary">
            <Input type="number" step="0.5" value={form.working_days_per_month}
              data-testid="roster-days-per-month"
              onChange={(e) => setForm({ ...form, working_days_per_month: e.target.value })} />
          </Field>
          <div className="col-span-2">
            <Field label="Notes">
              <Textarea rows={2} value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </Field>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-3 mt-3 border-t border-[#e4e4e7]">
          <BtnSecondary type="button" onClick={() => setOpen(false)}>Cancel</BtnSecondary>
          <BtnPrimary type="button" onClick={save} data-testid="roster-save">
            {edit ? "Save Changes" : "Create Roster"}
          </BtnPrimary>
        </div>
      </Modal>
    </div>
  );
}
